/* https://brandplatform.smartphoto.com/general/popups/my-photos-detail/ */
import { createPopup, IPopup } from "../technical/popup-new";
import { expressAddClass, expressEventListener, expressQuerySelector, expressRemoveClass } from "../../common/html";
import { createDetailedPhotoInfo, IDetailedPhotoInfo } from "../technical/detailed-photo-info";
import { addParameter, getParameter, updateParameter } from "../../utils/querystring";
import { getCookie } from "../../utils/cookie";
import { deletePhoto, IRotatePhoto, rotatePhotos } from "../../services/myphotos-service";
import { compositeDisposable, IDisposable } from "../../utils/disposable";
import { createCarrousel, ICarrousel } from "../molecules/carrousel";
import { ICustomPhotoSliderItem } from "../technical/customslider";

export interface IPhotoDetailDialog {
	readonly open: (photoId: string) => void;
	readonly dispose: () => void;
}

export interface IPhotoDetailDialogDependencies {
	readonly onBeforeClose: () => void;
	readonly removeGridItem: (itemId: string) => void;
}

export async function createPhotoDetailDialog(
	folderId: string,
	sortDirection: number,
	folderColumns: number,
	folderName: string,
	deps: IPhotoDetailDialogDependencies,
) {
	let popup: IPopup;
	let disposable: IDisposable;
	/*let visiblePhoto = 0;
	let visiblePhotoId = '';*/
	let detailedPhotoInfo: IDetailedPhotoInfo;
	let performedAction = false;
	const rotationRequests: IRotatePhoto[] = [];
	let infoIconEl: HTMLElement;
	let photoDetailsEl: HTMLElement;
	let carrousel: ICarrousel;

	const { onBeforeClose, removeGridItem } = deps;

	const init = async () => {
		const localOnBeforeClose = () => {
			if (performedAction) {
				if (rotationRequests && rotationRequests.length > 0) {
					rotatePhotos(rotationRequests).then((_) => onBeforeClose());
				} else {
					onBeforeClose();
				}
			}
		};

		popup = await createPopup({
			dialogId: "photoDetail",
			view: "PhotoDetail",
			forceRefresh: true,
			data: {
				folderId,
				sortDirection,
				folderColumns,
			},
			openClass: "is-popup-open",
			onBeforeClose: localOnBeforeClose,
		});

		if (popup && popup.popup) {
			addHandlers();
		}
	};

	const addHandlers = () => {
		// photo detailed info
		infoIconEl = expressQuerySelector<HTMLElement>(popup.popup, ".technical-photo-info", true);
		photoDetailsEl = expressQuerySelector<HTMLElement>(popup.popup, ".technical-photo-details", true);
		carrousel = createCarrousel(expressQuerySelector(popup.popup, ".m-carrousel--custom", true), {
			enableKeyboardNavigation: true,
			onNavigate: (photoId: string) => detailedPhotoInfo && detailedPhotoInfo.update(photoId),
		});

		const openDetailedInfo = () => {
			expressAddClass(popup.popup, "m-popup--detail-open");
			const closeDetailedInfo = () => {
				expressRemoveClass(popup.popup, "m-popup--detail-open");
			};
			detailedPhotoInfo = createDetailedPhotoInfo(
				photoDetailsEl,
				carrousel.getCurrentElement(0).template.dataset.photoid,
				folderName,
				{ onClose: closeDetailedInfo },
			);
		};

		disposable = compositeDisposable([expressEventListener(infoIconEl, "click", openDetailedInfo)]);

		// rotate photo
		const rotateIconEl = expressQuerySelector<HTMLElement>(popup.popup, ".technical-photo-rotate", true);
		const rotate = () => {
			const visiblePhotoEl = carrousel.getCurrentElement(0);
			const visiblePhotoId = visiblePhotoEl.template.dataset.photoid;
			const accId = getCookie("accid");
			if (visiblePhotoEl) {
				let transformUri: string;
				const photoEl = expressQuerySelector<HTMLElement>(popup.popup, "#" + visiblePhotoEl.id, true);
				const imgEl = expressQuerySelector<HTMLImageElement>(photoEl, "img", true);
				const uri = imgEl.src;
				const foundRotation = rotationRequests.find((el) => el.photoId === visiblePhotoId);
				if (uri.indexOf("transforms") > 0) {
					const rotation = getParameter("rot90", uri);
					if (rotation) {
						let rot = parseInt(rotation, 10);
						if (rot === 360) rot = 0;
						rot += 90;
						transformUri = updateParameter("rot90", rot + "", uri);
						if (foundRotation) foundRotation.rotation = rot;
						else rotationRequests.push({ photoId: visiblePhotoId, rotation: rot } as IRotatePhoto);
					}
					transformUri = updateParameter("upd", new Date().getTime() + "", transformUri);
				} else {
					transformUri =
						window.context.ecomApiUrl +
						"/mediastore/accounts/" +
						accId +
						"/photos/" +
						visiblePhotoId +
						"/transforms?streamname=medium&rot90=90";
					transformUri = addParameter("upd", new Date().getTime() + "", transformUri);
					if (foundRotation) foundRotation.rotation = 90;
					else rotationRequests.push({ photoId: visiblePhotoId, rotation: 90 } as IRotatePhoto);
				}

				performedAction = true;
				imgEl.src = transformUri;
				carrousel.getCurrentElement(0).template = photoEl;
			}
		};

		disposable.add(expressEventListener(rotateIconEl, "click", rotate));

		// Delete photo
		const deleteIconEl = expressQuerySelector<HTMLElement>(popup.popup, ".technical-photo-delete", true);

		const onDelete = () => {
			const visiblePhotoEl = carrousel.getCurrentElement(0);
			let okButtonDisposable: IDisposable | null;
			let cancelButtonDisposable: IDisposable | null;
			createPopup({
				dialogId: "DeletePhotos",
				view: "GenericPopup",
				forceRefresh: false,
				data: {
					ExtraPopupCss: "p-default",
					TitleLabel: "Label.MyPages.MyAlbums.Dialog.DeletePhoto.Title",
					BodyLabel: "Label.MyPages.MyAlbums.Dialog.DeletePhoto.Info",
					OkButtonLabel: "Label.MyPhotos.Dialogs.DeletePhotos.OkButtonText",
					CancelButtonLabel: "Label.MyPhotos.Dialogs.DeletePhotos.CancelButtonText",
				},
				onClose: (popupEl: HTMLElement) => {
					if (!popupEl) return;
					okButtonDisposable && okButtonDisposable.dispose();
					cancelButtonDisposable && cancelButtonDisposable.dispose();
				},
			}).then((res) => {
				if (!res.popup) return;
				const okButton = expressQuerySelector(res.popup, ".technical-general-popup-ok-button", true);
				const cancelButton = expressQuerySelector(res.popup, ".technical-general-popup-cancel-button", true);
				okButtonDisposable = expressEventListener(okButton, "click", (_) => {
					res.close();
					performedAction = true;
					const photoId = visiblePhotoEl.template.dataset.photoid;
					deletePhoto(photoId);
					removeGridItem(photoId);
					carrousel.removeSliderElement(0, visiblePhotoEl);
					const allSlides = carrousel.getAllElements(0);
					if (allSlides && allSlides.length === 0) popup.close();
					else carrousel.resize();
				});
				cancelButtonDisposable = expressEventListener(cancelButton, "click", (_) => res.close());
				res.openAsync();
			});
		};

		disposable.add(expressEventListener(deleteIconEl, "click", onDelete));
	};

	const open = (photoId: string) => {
		popup.openAsync().then((_) => {
			carrousel.resize();
			const visiblePhotoEl = carrousel
				.getAllElements(0)
				.find((el) => el.template.dataset.photoid === photoId) as ICustomPhotoSliderItem;
			if (visiblePhotoEl) {
				carrousel.goToSlide(visiblePhotoEl);
			}
		});
	};

	await init();

	return {
		open,
		dispose: () => disposable.dispose,
	} as IPhotoDetailDialog;
}
